import { useState } from 'react'
import type { Progress } from '@/domain/session'
import { exportProgress, importProgress } from '@/services/progressRepository'
import { cx } from '@/lib/cx'
import styles from './backup.module.css'

interface BackupDialogProps {
  progress: Progress
  onRestore: (progress: Progress) => Promise<void>
  onClose: () => void
}

/**
 * Copia de seguridad manual. El progreso vive en este móvil: si se borran los
 * datos del navegador, esta cadena es lo único que lo devuelve.
 */
export function BackupDialog({ progress, onRestore, onClose }: BackupDialogProps) {
  const code = exportProgress(progress)
  const [pasted, setPasted] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setMessage('Copiado. Guárdalo en tus notas.')
    } catch {
      setMessage('No se ha podido copiar: selecciona el texto a mano.')
    }
  }

  const restore = async () => {
    const restored = importProgress(pasted)
    if (!restored) {
      setMessage('Ese código no vale. Pégalo entero.')
      return
    }
    await onRestore(restored)
    setMessage(`Restaurado: ${restored.sessions.length} sesiones.`)
  }

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-label="Copia de seguridad">
      <div className={styles.panel}>
        <h2>Copia de seguridad</h2>
        <p className={styles.note}>
          Tus sesiones se guardan en este móvil. Copia este código y guárdalo donde quieras: con él
          recuperas el progreso si cambias de móvil o borras los datos del navegador.
        </p>

        <textarea className={styles.code} readOnly value={code} rows={4} onFocus={(e) => e.target.select()} />
        <button type="button" className={styles.action} onClick={() => void copy()}>
          Copiar código
        </button>

        <p className={styles.note}>¿Tienes un código de antes? Pégalo aquí y recupera tus sesiones.</p>
        <textarea
          className={styles.code}
          rows={3}
          value={pasted}
          placeholder="Pega aquí tu código"
          onChange={(event) => setPasted(event.target.value)}
        />
        <button type="button" className={styles.action} disabled={!pasted.trim()} onClick={() => void restore()}>
          Restaurar
        </button>

        {message && <p className={styles.message}>{message}</p>}

        <button type="button" className={cx(styles.action, styles.close)} onClick={onClose}>
          Cerrar
        </button>
      </div>
    </div>
  )
}
